import { useEffect } from "react";
import React from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import logo from "assets/images/BZU-logo.png";

// import Accordion from '@mui/material/Accordion';
// import AccordionDetails from '@mui/material/AccordionDetails';
// import Typography from '@mui/material/Typography';
// import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [expanded, setExpanded] = React.useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav } = controller; // Removed darkMode
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  let textColor = "black";

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(dispatch, true); // Always set to true for white sidebar
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, route, children }) => {
      let returnValue;

      if (type === "collapse") {
        returnValue = (
          <NavLink key={key} to={route}>
            <SidenavCollapse
              name={name}
              icon={<Icon sx={{ color: 'gray' }}>{icon}</Icon>}
              active={key === collapseName}
            />
          </NavLink>
        );
      } else if (type === "nested" && children) {
        const [isExpanded, setIsExpanded] = React.useState(false);
        const handleButtonClick = () => {
          setIsExpanded(!isExpanded);
        };
        const arrowIcon = isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />;
        returnValue = (
          <div key={key}>
            <SidenavCollapse
              name={name}
              icon={<Icon sx={{ color: 'gray' }}>{icon}</Icon>}
              endIcon={arrowIcon}
              active={key === collapseName}
              lastIcon={arrowIcon}
              onClick={handleButtonClick}
            />
            {isExpanded && (
              <List component="div" disablePadding sx={{ paddingLeft: 2 }}>
                {children.map(
                  ({
                    type: childType,
                    name: childName,
                    icon: childIcon,
                    key: childKey,
                    route: childRoute,
                  }) => (
                    <NavLink key={childKey} to={childRoute}>
                      <SidenavCollapse
                        name={childName}
                        icon={childIcon}
                        active={childKey === collapseName}
                      />
                    </NavLink>
                  )
                )}
              </List>
            )}
          </div>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider key={key} light={!transparentSidenav && whiteSidenav} />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav }}
    
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
         className="sidenav-logo"
        >
          <MDBox
            component="img"
            src={logo}
            alt="BZU-Logo"
            width="100%"
            height="100%"
          />
        </MDBox>
      </MDBox>
      <Divider light={!transparentSidenav && whiteSidenav} />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
